import {
  Button,
  Container,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  TextField,
  Box,
  Paper,
} from "@mui/material";
import ChakraImageComponent from "../../components/ChakraImageComponent";
import logo from "../../assets/logo1.png";
import { Link, useNavigate } from "react-router-dom";
import { useCreateTenant } from "../../hooks/query/useTenant";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  CreateTenantRequest,
  createTenantRequestSchema,
} from "../../utils/validations/tenant";
import { toast } from "react-toastify";
import { FcGoogle } from "react-icons/fc";

const RegisterPage = () => {
  const navigate = useNavigate();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const handleGoogleSignUp = () => {
    window.location.href = `${import.meta.env.VITE_API_URL}/v0/api/auth/google`;
  };

  const methods = useForm<CreateTenantRequest>({
    resolver: zodResolver(createTenantRequestSchema),
    defaultValues: {
      user: {
        name: "",
        email: "",
        phoneNumber: "",
        password: "",
      },
    },
  });

  const {
    handleSubmit,
    formState: { errors },
  } = methods;

  const { mutate: createTenant, isLoading } = useCreateTenant();

  const onSubmit = (data: CreateTenantRequest) => {
    toast.promise(
      new Promise((resolve, reject) => {
        try {
          createTenant(data, {
            onSuccess: (response) => {
              navigate("/register-success");
              resolve(response);
            },
            onError: (error: any) => {
              const errorMessage =
                error?.body?.error?.message ||
                // error?.message ||
                "An unknown error occurred.";
              console.log("Error mesage", errorMessage);

              reject(new Error(errorMessage));
            },
          });
        } catch (error: any) {
          const errorMessage =
            error?.body?.error?.message ||
            // error?.message ||
            "An unknown error occurred.";
          console.log("Error mesage", errorMessage);
          reject(new Error(errorMessage));
        }
      }),
      {
        pending: "Registering user...",
        success: "User created successfully! 🎉",
        error: {
          render({ data }: { data: any }) {
            console.log("Data", data.body);
            // 'data' is the error object from the rejected promise
            return data?.message || "Failed to create user. Please try again.";
          },
        },
      }
    );
  };

  return (
    <Container
      maxWidth="lg"
      sx={{ display: "flex", minHeight: "100vh", alignItems: "center" }}
    >
      <Grid
        container
        spacing={12}
        sx={{
          height: "100%",
          display: "flex",
        }}
      >
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: isSmallScreen ? "none" : "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 2,
            height: "100vh",
          }}
        >
          <ChakraImageComponent />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 2,
          }}
        >
          <Paper
            elevation={10}
            sx={{
              width: "100%",
              maxWidth: "450px",
              borderRadius: theme.spacing(3),
              padding: theme.spacing(4),
              background: "linear-gradient(145deg, #f0f4f8 0%, #e6eaf0 100%)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: "-50px",
                left: "-50px",
                width: "150px",
                height: "150px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #bb0000 0%, #bb0000 100%)",
                opacity: 0.1,
              }}
            />
            <Box
              sx={{
                textAlign: "center",
                marginBottom: theme.spacing(3),
              }}
            >
              <img
                src={logo}
                alt="logo"
                style={{
                  width: "200px",
                  height: "200px",
                  marginBottom: theme.spacing(1),
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  marginBottom: theme.spacing(1),
                }}
              >
                Create Account
              </Typography>
              {/* <Typography variant="body2" color="textSecondary">
                Join our community and start your journey
              </Typography> */}
            </Box>

            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="email"
                      label="Email"
                      size="small"
                      type="email"
                      variant="outlined"
                      {...methods.register("user.email")}
                      error={Boolean(errors.user?.email)}
                      helperText={errors.user?.email?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: theme.spacing(2),
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="userName"
                      label="Name"
                      size="small"
                      variant="outlined"
                      {...methods.register("user.name")}
                      error={Boolean(errors.user?.name)}
                      helperText={errors.user?.name?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: theme.spacing(2),
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      margin="normal"
                      fullWidth
                      id="phoneNumber"
                      label="Phone Number"
                      size="small"
                      variant="outlined"
                      {...methods.register("user.phoneNumber")}
                      error={Boolean(errors.user?.phoneNumber)}
                      helperText={errors.user?.phoneNumber?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: theme.spacing(2),
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="password"
                      label="Password"
                      size="small"
                      type="password"
                      variant="outlined"
                      {...methods.register("user.password")}
                      error={Boolean(errors.user?.password)}
                      helperText={errors.user?.password?.message}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: theme.spacing(2),
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={isLoading}
                      sx={{
                        mt: 2,
                        mb: 2,
                        borderRadius: theme.spacing(2),
                        padding: theme.spacing(1.5),
                        background:
                          "linear-gradient(45deg, #bb0000 0%, #bb0000 100%)",
                        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                        transition: "transform 0.3s ease",
                        "&:hover": {
                          transform: "scale(1.02)",
                          boxShadow: "0 6px 8px rgba(0,0,0,0.15)",
                        },
                      }}
                    >
                      {isLoading ? "Registering..." : "Register"}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </FormProvider>

            {/* "Sign up with Google" Button */}
            <Button
              fullWidth
              variant="outlined"
              onClick={handleGoogleSignUp}
              startIcon={<FcGoogle />}
              sx={{
                mt: 1,
                mb: 1,
                borderRadius: theme.spacing(2),
                padding: theme.spacing(1.5),
                border: "2px solid #bb0000",
                color: "#bb0000",
                fontWeight: 600,
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                  backgroundColor: "#bb0000",
                  color: "#fff",
                },
              }}
            >
              Sign up with Google
            </Button>

            <Typography
              variant="body2"
              align="center"
              sx={{
                mt: 2,
                color: theme.palette.text.secondary,
              }}
            >
              Already have an account?{" "}
              <Link
                to="/login"
                style={{
                  textDecoration: "none",
                  color: "#bb0000",
                  fontWeight: 600,
                }}
              >
                Login
              </Link>
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default RegisterPage;
